import React, { useRef } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { CalendarDays, LayoutDashboard, Search, ShoppingCart, Users } from 'lucide-react';

const featureList = [
    {
        title: 'Dashboard Inteligente',
        description:
            'Visao panoramica de toda a sua operacao em tempo real. Tome decisoes rapidas com dados consolidados e metricas de performance instantaneas.',
        icon: LayoutDashboard,
        color: 'from-blue-500/20 to-blue-600/5',
        accent: 'text-blue-300',
        renderPreview: () => (
            <div className="rounded-[30px] border border-white/10 bg-[#0f131a] p-4 shadow-[0_24px_60px_rgba(0,0,0,0.28)]">
                <div className="grid gap-3 sm:grid-cols-3">
                    {[
                        ['Receita', 'R$ 8.420'],
                        ['Pedidos', '32'],
                        ['Ticket medio', 'R$ 71'],
                    ].map(([label, value]) => (
                        <div key={label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-3">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-white/35">{label}</p>
                            <p className="mt-2 text-lg font-semibold text-white">{value}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 rounded-[26px] border border-white/8 bg-white/[0.03] p-4">
                    <div className="mb-4 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-semibold text-white">Performance semanal</p>
                            <p className="text-xs text-white/40">Faturamento por canal</p>
                        </div>
                        <div className="rounded-full bg-white/[0.05] px-3 py-1 text-[11px] text-white/50">7 dias</div>
                    </div>
                    <div className="flex h-32 items-end gap-2">
                        {[40, 64, 58, 80, 72, 88, 55].map((height, index) => (
                            <div key={index} className="flex flex-1 flex-col items-center gap-2">
                                <div
                                    className="w-full rounded-t-2xl bg-gradient-to-t from-primary to-orange-300/90"
                                    style={{ height: `${height}%` }}
                                />
                                <span className="text-[10px] text-white/30">{['S', 'T', 'Q', 'Q', 'S', 'S', 'D'][index]}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {[
                        ['Pedidos pendentes', '08 aguardando acao'],
                        ['Reservas ativas', '14 entre hoje e amanha'],
                    ].map(([label, meta]) => (
                        <div key={label} className="rounded-2xl border border-white/8 bg-[#0b0e13] p-3">
                            <p className="text-sm font-semibold text-white">{label}</p>
                            <p className="mt-1 text-xs text-white/40">{meta}</p>
                        </div>
                    ))}
                </div>
            </div>
        ),
    },
    {
        title: 'Gestao de Pedidos',
        description:
            'Sincronizacao perfeita entre salao, cozinha e delivery. Acabe com atrasos e erros de comunicacao com um fluxo de pedidos otimizado.',
        icon: ShoppingCart,
        color: 'from-primary/20 to-primary-hover/5',
        accent: 'text-primary',
        renderPreview: () => (
            <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-3">
                {[
                    {
                        title: 'Novo pedido',
                        items: ['Mesa 08', '2x Burger Smash', 'Tempo 02 min'],
                    },
                    {
                        title: 'Em preparo',
                        items: ['Delivery Juliana', 'Risoto + salada', 'Tempo 14 min'],
                    },
                    {
                        title: 'Pronto',
                        items: ['Retirada Bruno', 'Yakisoba + refrigerante', 'Aguardando retirada'],
                    },
                ].map((column, index) => (
                    <div key={column.title} className="rounded-[28px] border border-white/10 bg-[#10141a] p-4">
                        <div className="mb-4 flex items-center justify-between">
                            <p className="text-sm font-semibold text-white">{column.title}</p>
                            <span className="rounded-full bg-primary/12 px-2.5 py-1 text-[11px] text-primary">
                                0{index + 2}
                            </span>
                        </div>
                        <div className="space-y-3">
                            {column.items.map((item) => (
                                <div key={item} className="rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-2.5 text-sm text-white/75">
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                </div>
                <div className="rounded-[28px] border border-white/10 bg-[#10141a] p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-white">Detalhe do pedido #4021</p>
                        <span className="rounded-full bg-white/[0.05] px-2.5 py-1 text-[11px] text-white/55">R$ 128,00</span>
                    </div>
                    <div className="space-y-2">
                        {[
                            '2x Burger Smash',
                            '1x Fritas rusticas',
                            '1x Refrigerante zero',
                        ].map((item) => (
                            <div key={item} className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-2.5 text-sm">
                                <span className="text-white/80">{item}</span>
                                <span className="text-white/35">ok</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: 'Clientes & CRM',
        description:
            'Conheca quem frequenta seu restaurante. Crie fidelidade com historico de consumo, preferencias e campanhas de marketing direcionadas.',
        icon: Users,
        color: 'from-emerald-500/20 to-emerald-600/5',
        accent: 'text-emerald-300',
        renderPreview: () => (
            <div className="rounded-[30px] border border-white/10 bg-[#0f1514] p-4">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-semibold text-white">Clientes do restaurante</p>
                        <p className="text-xs text-white/40">Historico e recorrencia</p>
                    </div>
                    <span className="rounded-full bg-emerald-500/12 px-3 py-1 text-[11px] text-emerald-300">CRM ativo</span>
                </div>
                <div className="relative mb-3">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/25" />
                    <div className="rounded-2xl border border-white/8 bg-white/[0.03] py-3 pl-10 pr-4 text-sm text-white/35">
                        Buscar cliente...
                    </div>
                </div>
                <div className="space-y-3">
                    {[
                        ['Maria Oliveira', '6 pedidos no mes', 'VIP'],
                        ['Joao Silva', 'Ultimo pedido ontem', 'Retorno'],
                        ['Camila Torres', 'Prefere delivery', 'Promocao'],
                    ].map(([name, meta, tag]) => (
                        <div key={name} className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] p-3">
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-2xl bg-emerald-500/14" />
                                <div>
                                    <p className="text-sm font-semibold text-white">{name}</p>
                                    <p className="text-xs text-white/40">{meta}</p>
                                </div>
                            </div>
                            <span className="rounded-full bg-white/[0.05] px-2.5 py-1 text-[11px] text-emerald-300">{tag}</span>
                        </div>
                    ))}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/8 bg-[#0b1211] p-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-white/35">Pedidos</p>
                        <p className="mt-2 text-xl font-semibold text-white">146</p>
                    </div>
                    <div className="rounded-2xl border border-white/8 bg-[#0b1211] p-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-white/35">Total gasto</p>
                        <p className="mt-2 text-xl font-semibold text-emerald-300">R$ 12.480</p>
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: 'Reservas Automaticas',
        description:
            'Elimine o conflito de horarios. Centralize suas reservas online e presenciais com confirmacao automatica para o cliente via WhatsApp.',
        icon: CalendarDays,
        color: 'from-purple-500/20 to-purple-600/5',
        accent: 'text-purple-300',
        renderPreview: () => (
            <div className="rounded-[30px] border border-white/10 bg-[#121019] p-4">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-semibold text-white">Agenda do jantar</p>
                        <p className="text-xs text-white/40">Quarta, 19:30</p>
                    </div>
                    <span className="rounded-full bg-purple-500/12 px-3 py-1 text-[11px] text-purple-300">7 reservas</span>
                </div>
                <div className="mb-3 flex gap-2 text-[11px]">
                    <span className="rounded-full bg-yellow-500/12 px-2.5 py-1 text-yellow-300">2 pendentes</span>
                    <span className="rounded-full bg-emerald-500/12 px-2.5 py-1 text-emerald-300">5 confirmadas</span>
                </div>
                <div className="space-y-3">
                    {[
                        ['19:30', 'Mesa 05 - Helena', '4 pessoas'],
                        ['20:00', 'Salao interno - Fabio', '2 pessoas'],
                        ['20:30', 'Mesa 11 - Juliana', '6 pessoas'],
                    ].map(([time, guest, size]) => (
                        <div key={time + guest} className="grid grid-cols-[70px_1fr_auto] items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
                            <span className="text-sm font-semibold text-purple-300">{time}</span>
                            <div>
                                <p className="text-sm font-semibold text-white">{guest}</p>
                                <p className="text-xs text-white/40">Confirmado via WhatsApp</p>
                            </div>
                            <span className="text-xs text-white/50">{size}</span>
                        </div>
                    ))}
                </div>
                <div className="mt-4 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
                    <p className="text-sm font-semibold text-white">Observacao</p>
                    <p className="mt-1 text-xs leading-relaxed text-white/45">
                        Cliente Juliana solicitou mesa interna e cadeirao infantil.
                    </p>
                </div>
            </div>
        ),
    },
];

interface FeaturesProps {
    onOpenDemo?: (title?: string) => void;
}

const FeatureItem = ({ feature, index, onOpenDemo }: { feature: (typeof featureList)[number]; index: number; onOpenDemo?: (title?: string) => void }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start']
    });

    const y = useTransform(scrollYProgress, [0, 0.5, 1], [70, 0, -70]);
    const opacity = useTransform(scrollYProgress, [0, 0.18, 0.82, 1], [0, 1, 1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.96]);

    const springY = useSpring(y, { stiffness: 110, damping: 28 });
    const springScale = useSpring(scale, { stiffness: 110, damping: 28 });

    return (
        <motion.div
            ref={ref}
            style={{ y: springY, opacity, scale: springScale }}
            className={`flex flex-col items-start gap-10 lg:flex-row lg:items-center lg:gap-16 ${
                index % 2 !== 0 ? 'lg:flex-row-reverse' : ''
            }`}
        >
            <div className="w-full flex-1 space-y-6">
                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br ${feature.color}`}>
                    <feature.icon className={`h-8 w-8 ${feature.accent}`} />
                </div>
                <div className="space-y-4">
                    <h3 className="text-4xl font-black tracking-tighter text-white md:text-5xl">{feature.title}</h3>
                    <p className="max-w-xl text-lg leading-relaxed text-secondary md:text-xl">{feature.description}</p>
                </div>
                <button 
                    onClick={() => onOpenDemo ? onOpenDemo(`Interesse em ${feature.title}`) : null}
                    className="group flex items-center gap-2 text-base font-bold text-primary transition-all hover:gap-4"
                >
                    Saiba mais <span className="transition-transform group-hover:translate-x-1">-&gt;</span>
                </button>
            </div>

            <div className="w-full flex-1">
                <div className={`rounded-[38px] border border-white/10 bg-gradient-to-br ${feature.color} p-[1px]`}>
                    <div className="rounded-[38px] bg-[#0b0d12]/96 p-4 md:p-5">{feature.renderPreview()}</div>
                </div>
            </div>
        </motion.div>
    );
};

const Features: React.FC<FeaturesProps> = ({ onOpenDemo }) => {
    return (
        <section className="bg-background px-5 py-20 md:px-8 md:py-28">
            <div className="mx-auto max-w-[1400px] space-y-20 md:space-y-28">
                <div className="max-w-3xl space-y-5">
                    <h2 className="text-4xl font-black tracking-tighter text-white md:text-6xl">
                        Tudo o que você precisa <br />
                        <span className="text-primary">sem complicações.</span>
                    </h2>
                </div>

                <div className="space-y-24 md:space-y-32">
                    {featureList.map((feature, index) => (
                        <FeatureItem key={feature.title} feature={feature} index={index} onOpenDemo={onOpenDemo} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Features;
